import { Typography } from "@/components";
import useProfile from "@/hooks/useProfile";
import { cn } from "@/libraries/cn";
import { CASE_MEMBER_TYPE } from "@/libraries/constant";
import {
	getMessagesQueryOptions,
	listWoundAnalysesQueryOptions,
} from "@/queries";
import { MESSAGE_TAG } from "@/schemas";
import type { Case } from "@/types";
import type { WoundAnalysis } from "@/types/askhemi";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface CaseAiPanelProps {
	caseItem: Case;
}

interface ResolvedShare {
	shareId: number;
	expiresAt: string | null;
	photosCount: number;
	trackingDays: number | null;
	site: string | null;
	createdAt: string;
	isActive: boolean;
	label: string;
}

const CaseAiPanel = ({ caseItem }: CaseAiPanelProps) => {
	const { t } = useTranslation(["askhemi", "common"]);
	const navigate = useNavigate();
	const { profile } = useProfile();

	// External members do not have access to wound analysis endpoints
	const isExternal =
		caseItem.member_type === CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER ||
		Boolean(
			profile && caseItem.external_members?.some((m) => m.id === profile.id),
		);

	// Query AskHEMI Files messages to resolve available shares on this case
	const messagesQuery = useInfiniteQuery(
		getMessagesQueryOptions({
			channel_name: String(caseItem.channel_name),
			tags: MESSAGE_TAG.ASKHEMI,
		}),
	);

	const allMessages = useMemo(() => {
		return messagesQuery.data?.pages?.flatMap((p) => p.messages) ?? [];
	}, [messagesQuery.data]);

	const shares = useMemo<ResolvedShare[]>(() => {
		const result: ResolvedShare[] = [];
		const seen = new Set<number>();
		for (const msg of allMessages) {
			if (msg.is_deleted || msg.feature !== "askhemi_share") continue;
			const s = msg.structured_data;
			const id = s?.health_image_share_id;
			if (!id || seen.has(id)) continue;
			seen.add(id);
			const expiresAt = s.expires_at ?? null;
			const isActive = !expiresAt || dayjs(expiresAt).isAfter(dayjs());
			const photosCount = s.photos_count ?? 0;
			const trackingDays = s.tracking_days ?? null;
			const site = s.site ?? null;

			let label = site ?? "";
			if (photosCount) {
				label += `${label ? " · " : ""}${photosCount} photos`;
			}
			if (trackingDays) {
				label += ` (${trackingDays} days)`;
			}
			if (!label) {
				label = `Series #${id}`;
			}
			if (!isActive) {
				label += " (Expired)";
			}

			result.push({
				shareId: id,
				expiresAt,
				photosCount,
				trackingDays,
				site,
				createdAt: msg.created_at,
				isActive,
				label,
			});
		}
		return result;
	}, [allMessages]);

	const activeShares = useMemo(
		() => shares.filter((s) => s.isActive),
		[shares],
	);

	const [selectedShareId, setSelectedShareId] = useState<number | null>(null);

	useEffect(() => {
		if (activeShares.length > 0) {
			if (
				!selectedShareId ||
				!activeShares.some((s) => s.shareId === selectedShareId)
			) {
				setSelectedShareId(activeShares[0].shareId);
			}
		} else {
			setSelectedShareId(null);
		}
	}, [activeShares, selectedShareId]);

	// Query past wound analyses for this case
	const analysesQuery = useQuery(listWoundAnalysesQueryOptions(caseItem.id));
	const rawAnalyses = analysesQuery.data ?? [];

	const analyses = useMemo<WoundAnalysis[]>(() => {
		return [...rawAnalyses].sort(
			(a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf(),
		);
	}, [rawAnalyses]);

	if (isExternal) return null;

	const handleOpenAnalysis = (shareIdToOpen?: number | null) => {
		const targetShareId = shareIdToOpen ?? selectedShareId;
		if (!targetShareId) return;
		navigate({
			to: "/dashboard/case/view/$id/wound-analysis",
			params: { id: caseItem.id },
			search: { shareId: targetShareId },
		});
	};

	return (
		<div className="container max-w-3xl w-full py-3 flex flex-col gap-3 shrink-0">
			{/* Card 1: HEMI AI on this case */}
			<div className="rounded-xl border border-zinc-200/80 bg-white p-4 shadow-sm">
				<h2 className="flex items-center gap-2 text-base font-bold text-violet-700">
					<AutoAwesomeRoundedIcon fontSize="small" />
					{t("aiTab.cardTitle")}
				</h2>
				<hr className="my-2.5 border-zinc-200" />
				<Typography
					fontSize={13.5}
					className="!text-zinc-600 mb-3 leading-relaxed"
				>
					{t("aiTab.lead")}
				</Typography>

				{activeShares.length > 1 ? (
					<div className="mb-3">
						<label
							htmlFor="share-series-select"
							className="mb-1 block text-xs font-semibold text-zinc-500"
						>
							{t("aiTab.selectSeries")}
						</label>
						<select
							id="share-series-select"
							value={selectedShareId ?? ""}
							onChange={(e) => setSelectedShareId(Number(e.target.value))}
							className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs text-zinc-800 focus:border-violet-500 focus:outline-none"
						>
							{activeShares.map((s) => (
								<option key={s.shareId} value={s.shareId}>
									{s.label}
								</option>
							))}
						</select>
					</div>
				) : null}

				<button
					type="button"
					disabled={!selectedShareId}
					onClick={() => handleOpenAnalysis()}
					className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
				>
					<AutoAwesomeRoundedIcon fontSize="small" />
					{t("woundCareAnalysis")}
				</button>
				{!selectedShareId ? (
					<Typography fontSize={12} className="mt-2 text-center !text-zinc-500">
						{t("aiTab.noPhotosShared")}
					</Typography>
				) : null}
			</div>

			{/* Card 2: Recent */}
			<div className="rounded-xl border border-zinc-200/80 bg-white p-4 shadow-sm">
				<h2 className="text-base font-bold text-zinc-900">
					{t("aiTab.recentTitle")}
				</h2>
				<hr className="my-2.5 border-zinc-200" />

				{analysesQuery.isLoading ? (
					<div className="h-16 animate-pulse rounded-lg bg-zinc-100" />
				) : analyses.length === 0 ? (
					<Typography
						fontSize={13.5}
						className="!text-zinc-600 leading-relaxed m-0"
					>
						{t("aiTab.emptyRecent")}
					</Typography>
				) : (
					<div className="flex flex-col divide-y divide-zinc-100 max-h-48 overflow-y-auto">
						{analyses.map((a) => {
							const targetShareId = a.askhemi_share_id ?? selectedShareId;
							return (
								<div
									key={a.id}
									role={targetShareId ? "button" : undefined}
									tabIndex={targetShareId ? 0 : undefined}
									onClick={() => handleOpenAnalysis(targetShareId)}
									onKeyDown={(e) => {
										if ((e.key === "Enter" || e.key === " ") && targetShareId) {
											handleOpenAnalysis(targetShareId);
										}
									}}
									className={cn(
										"flex items-center justify-between py-2.5 px-2 rounded-lg transition-colors",
										targetShareId ? "cursor-pointer hover:bg-violet-50/60" : "",
									)}
								>
									<div className="flex flex-col gap-0.5">
										<div className="flex items-center gap-2">
											<span className="text-xs font-semibold text-zinc-900">
												{dayjs(a.created_at).format("DD MMM YYYY, hh:mm A")}
											</span>
											{a.photos_count != null ? (
												<span className="text-xs text-zinc-500">
													· {t("aiTab.photosCount", { count: a.photos_count })}
												</span>
											) : null}
										</div>
										<span className="text-xs text-zinc-500">
											{t("aiTab.runByPrefix")}
											<span className="font-medium text-zinc-700">
												{a.requested_by_name || "-"}
											</span>
										</span>
									</div>
									<span
										className={cn(
											"rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize",
											a.status === "complete" &&
												"bg-emerald-100 text-emerald-800",
											(a.status === "pending" || a.status === "processing") &&
												"bg-amber-100 text-amber-800",
											a.status === "failed" && "bg-rose-100 text-rose-800",
										)}
									>
										{t(`aiTab.status.${a.status}`, {
											defaultValue: a.status,
										})}
									</span>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default CaseAiPanel;
